import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { MaskedInput } from '@/components/ui/masked-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DualListBox } from '@/components/ui/dual-listbox';
import { LocationAccountCombobox } from '../LocationAccountCombobox';
import { LocationImageUpload } from '../LocationImageUpload';
import { Loader2, MapPin } from 'lucide-react';
import {
  useLocationAccount,
  useLocationFormOptions,
  useLocationOptions,
} from '../../hooks/useLocations';
import type {
  CreateLocationInput,
  Transmitter,
  Sensor,
} from '../../types';
import { LOCATION_TAGS } from '../../types';

const ALL_ACCOUNTS_VALUE = 'all';

interface CreateLocationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateLocationInput) => Promise<void>;
  isSubmitting: boolean;
  defaultAccountId?: string;
  onOpenGpsPicker?: (lat?: number, lng?: number) => void;
}

/**
 * Get display label for a transmitter
 */
function getTransmitterLabel(t: Transmitter): string {
  return t.name || t.dev_id || t.customid || t._id;
}

/**
 * Get sub-label for a transmitter
 */
function getTransmitterSubLabel(t: Transmitter): string {
  if (t.battery_voltage) {
    return `${t.dev_id || ''} • ${t.battery_voltage}V`;
  }
  return t.dev_id || '';
}

/**
 * Get display label for a sensor
 */
function getSensorLabel(s: Sensor): string {
  return s.name || s.search_name || s._id;
}

export function CreateLocationModal({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
  defaultAccountId,
  onOpenGpsPicker,
}: CreateLocationModalProps) {
  const { t } = useTranslation('locations');

  // Form state
  const [accountId, setAccountId] = useState(defaultAccountId || ALL_ACCOUNTS_VALUE);
  const [siteId, setSiteId] = useState<string | undefined>(undefined);
  const [name, setName] = useState('');
  const [abbreviations, setAbbreviations] = useState('');
  const [roomNumber, setRoomNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [extension, setExtension] = useState('');
  const [fax, setFax] = useState('');
  const [faxExtension, setFaxExtension] = useState('');
  const [description, setDescription] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [selectedTransmitters, setSelectedTransmitters] = useState<string[]>([]);
  const [selectedSensors, setSelectedSensors] = useState<string[]>([]);

  const selectedAccountId = accountId !== ALL_ACCOUNTS_VALUE ? accountId : '';
  const { data: selectedAccount } = useLocationAccount(
    selectedAccountId || undefined,
    open && !!selectedAccountId
  );
  const { data: locationOptions, isLoading: isLoadingSites } = useLocationOptions(
    selectedAccountId || undefined,
    open && !!selectedAccountId
  );
  const mongoOrgId = selectedAccount?.mongoOrganisationId;

  // Fetch form options for transmitters/sensors when we have a mongoOrgId
  const { data: formOptions, isLoading: isLoadingFormOptions } = useLocationFormOptions(mongoOrgId);

  const sites = selectedAccountId ? locationOptions?.sites || [] : [];
  const transmitters: Transmitter[] = formOptions?.transmitters || [];
  const sensors: Sensor[] = formOptions?.sensors || [];

  useEffect(() => {
    if (open) {
      setAccountId(defaultAccountId || ALL_ACCOUNTS_VALUE);
    }
  }, [defaultAccountId, open]);

  // Reset form when modal closes
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setAccountId(defaultAccountId || ALL_ACCOUNTS_VALUE);
      setSiteId(undefined);
      setName('');
      setAbbreviations('');
      setRoomNumber('');
      setPhone('');
      setExtension('');
      setFax('');
      setFaxExtension('');
      setDescription('');
      setSelectedTags([]);
      setLatitude('');
      setLongitude('');
      setPendingImageFile(null);
      setSelectedTransmitters([]);
      setSelectedSensors([]);
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
        setImagePreviewUrl(null);
      }
    }
    onOpenChange(isOpen);
  };

  // Clean up preview URL on unmount
  useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  // Reset dependent selections when account changes
  useEffect(() => {
    setSiteId(undefined);
    setSelectedTransmitters([]);
    setSelectedSensors([]);
  }, [accountId]);

  const handleAccountChange = (value: string) => {
    setAccountId(value);
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleImageSelect = (file: File) => {
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    setPendingImageFile(file);
    setImagePreviewUrl(URL.createObjectURL(file));
  };

  const handleImageRemove = () => {
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    setPendingImageFile(null);
    setImagePreviewUrl(null);
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLatitude(pos.coords.latitude.toFixed(6));
        setLongitude(pos.coords.longitude.toFixed(6));
      },
      () => {
        // Silently fail - user can enter manually
      }
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedAccountId || !name.trim()) {
      return;
    }

    const data: CreateLocationInput = {
      accountId: selectedAccountId,
      siteId: siteId || undefined,
      name: name.trim(),
      abbreviations: abbreviations
        .split(',')
        .map((a) => a.trim())
        .filter(Boolean),
      roomNumber: roomNumber.trim() || undefined,
      phone: phone.trim() || undefined,
      extension: extension.trim() || undefined,
      fax: fax.trim() || undefined,
      faxExtension: faxExtension.trim() || undefined,
      description: description.trim() || undefined,
      tags: selectedTags.length > 0 ? selectedTags : undefined,
      latitude: latitude ? parseFloat(latitude) : undefined,
      longitude: longitude ? parseFloat(longitude) : undefined,
      transmitters: selectedTransmitters.length > 0 ? selectedTransmitters : undefined,
      sensors: selectedSensors.length > 0 ? selectedSensors : undefined,
    };

    await onSubmit(data);

    // If there's a pending image, upload it after creation
    // Note: The parent will handle closing the modal after creation
    // Image upload happens as a follow-up since we need the location ID
    if (pendingImageFile) {
      // The image will be uploaded separately after the location is created
      // This is handled by the parent component
    }

    handleOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('create.title', 'Create Location')}</DialogTitle>
          <DialogDescription>
            {t('create.description', 'Add a new location to your account.')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {/* Account selector */}
            <div className="grid gap-2">
              <Label>{t('create.account', 'Account')} *</Label>
              <LocationAccountCombobox
                value={accountId}
                onChange={handleAccountChange}
                disabled={isSubmitting}
                placeholder={t('create.selectAccount', 'Select an account')}
              />
            </div>

            {/* Site selector */}
            <div className="grid gap-2">
              <Label htmlFor="site">{t('create.site', 'Site')}</Label>
              <Select
                value={siteId || 'none'}
                onValueChange={(val) => setSiteId(val === 'none' ? undefined : val)}
                disabled={!selectedAccountId}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('create.selectSite', 'Select a site (optional)')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('create.noSite', 'No site')}</SelectItem>
                  {!isLoadingSites &&
                    sites.map((site) => (
                      <SelectItem key={site.id} value={site.id}>
                        {site.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Name */}
            <div className="grid gap-2">
              <Label htmlFor="name">{t('create.name', 'Name')} *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('create.namePlaceholder', 'e.g., Lab Room 101')}
                required
              />
            </div>

            {/* Room Number */}
            <div className="grid gap-2">
              <Label htmlFor="roomNumber">{t('create.roomNumber', 'Room Number')}</Label>
              <Input
                id="roomNumber"
                value={roomNumber}
                onChange={(e) => setRoomNumber(e.target.value)}
                placeholder={t('create.roomNumberPlaceholder', 'e.g., 101')}
              />
            </div>

            {/* Abbreviations */}
            <div className="grid gap-2">
              <Label htmlFor="abbreviations">{t('create.abbreviations', 'Abbreviations')}</Label>
              <Input
                id="abbreviations"
                value={abbreviations}
                onChange={(e) => setAbbreviations(e.target.value)}
                placeholder={t('create.abbreviationsPlaceholder', 'Comma-separated, e.g., LAB, L101')}
              />
            </div>

            {/* Image Upload */}
            <div className="grid gap-2">
              <Label>{t('form.image', 'Image')}</Label>
              <LocationImageUpload
                imageUrl={imagePreviewUrl}
                onUpload={handleImageSelect}
                onDelete={handleImageRemove}
                isLoading={false}
                disabled={isSubmitting}
              />
            </div>

            {/* Contact info - Phone */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="phone">{t('create.phone', 'Phone')}</Label>
                <MaskedInput
                  mask="(000) 000-0000"
                  id="phone"
                  value={phone}
                  onAccept={(val) => setPhone(val)}
                  placeholder="(514) 555-1234"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="extension">{t('create.extension', 'Ext.')}</Label>
                <Input
                  id="extension"
                  value={extension}
                  onChange={(e) => setExtension(e.target.value)}
                />
              </div>
            </div>

            {/* Contact info - Fax */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="fax">{t('create.fax', 'Fax')}</Label>
                <MaskedInput
                  mask="(000) 000-0000"
                  id="fax"
                  value={fax}
                  onAccept={(val) => setFax(val)}
                  placeholder="(514) 555-1234"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="faxExtension">{t('create.faxExtension', 'Ext.')}</Label>
                <Input
                  id="faxExtension"
                  value={faxExtension}
                  onChange={(e) => setFaxExtension(e.target.value)}
                />
              </div>
            </div>

            {/* Coordinates / GPS */}
            <div className="grid gap-2">
              <Label>{t('create.coordinates', 'Coordinates')}</Label>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="number"
                  step="any"
                  placeholder={t('create.latitude', 'Latitude')}
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value)}
                />
                <Input
                  type="number"
                  step="any"
                  placeholder={t('create.longitude', 'Longitude')}
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                {onOpenGpsPicker && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onOpenGpsPicker(
                      latitude ? parseFloat(latitude) : undefined,
                      longitude ? parseFloat(longitude) : undefined
                    )}
                  >
                    <MapPin className="h-4 w-4 mr-1" />
                    {t('form.openGpsPicker', 'Pick on Map')}
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleUseCurrentLocation}
                >
                  <MapPin className="h-4 w-4 mr-1" />
                  {t('form.useCurrentLocation', 'Current Location')}
                </Button>
              </div>
            </div>

            {/* Description */}
            <div className="grid gap-2">
              <Label>{t('create.description', 'Description')}</Label>
              <RichTextEditor
                value={description}
                onChange={setDescription}
                placeholder={t('create.description', 'Description')}
                disabled={isSubmitting}
                minHeight="60px"
              />
            </div>

            {/* Transmitters / Sensors (only when account is selected) */}
            {selectedAccountId && mongoOrgId && (
              <>
                {isLoadingFormOptions ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <>
                    {transmitters.length > 0 && (
                      <div className="space-y-2">
                        <Label>{t('form.transmitters', 'Transmitters')}</Label>
                        <DualListBox
                          available={transmitters}
                          selected={selectedTransmitters}
                          onSelectionChange={setSelectedTransmitters}
                          getItemId={(item) => item._id}
                          getItemLabel={getTransmitterLabel}
                          getItemSubLabel={getTransmitterSubLabel}
                          availableLabel={t('form.availableTransmitters', 'Available')}
                          selectedLabel={t('form.selectedTransmitters', 'Assigned')}
                          searchPlaceholder={t('form.searchTransmitters', 'Search transmitters...')}
                          listHeight={150}
                          disabled={isSubmitting}
                        />
                      </div>
                    )}

                    {sensors.length > 0 && (
                      <div className="space-y-2">
                        <Label>{t('form.sensors', 'Sensors')}</Label>
                        <DualListBox
                          available={sensors}
                          selected={selectedSensors}
                          onSelectionChange={setSelectedSensors}
                          getItemId={(item) => item._id}
                          getItemLabel={getSensorLabel}
                          availableLabel={t('form.availableSensors', 'Available')}
                          selectedLabel={t('form.selectedSensors', 'Assigned')}
                          searchPlaceholder={t('form.searchSensors', 'Search sensors...')}
                          listHeight={150}
                          disabled={isSubmitting}
                        />
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* Tags */}
            <div className="grid gap-2">
              <Label>{t('create.tags', 'Tags')}</Label>
              <div className="flex flex-wrap gap-3">
                {LOCATION_TAGS.map((tag) => (
                  <div key={tag} className="flex items-center space-x-2">
                    <Checkbox
                      id={`create-tag-${tag}`}
                      checked={selectedTags.includes(tag)}
                      onCheckedChange={() => handleTagToggle(tag)}
                    />
                    <label
                      htmlFor={`create-tag-${tag}`}
                      className="text-sm cursor-pointer"
                    >
                      {tag}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              {t('create.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting || !selectedAccountId || !name.trim()}>
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {t('create.submit', 'Create Location')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
